import React, { useState, useEffect } from "react"
import axios from "axios"
import { useNavigate, useParams, Link } from "react-router-dom"
import { FaAngleLeft, FaDownload, FaEdit, FaFilePdf, FaPlus } from 'react-icons/fa'
import Alert from 'react-bootstrap/Alert'
import { apiURL, filesURL } from 'config/globals'
import Topbar from 'components/Topbar'
import Footer from 'components/Footer'
import { checkAuth } from 'utils/Auth'
import { Badge, Button, Card, Col, FloatingLabel, Form, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap"

const statusOptions = [
    { id: 'draft', label: 'Draft'},
    { id: 'review', label: 'Submitted for review'},
    { id: 'published', label: 'Published'},
]

const BuildingList = () => {
    const [currentUser, setCurrentUser] = useState()
    const [museum, setMuseum] = useState({})
    const [buildings, setBuildings] = useState([])
    const [filteredBuildings, setFilteredBuildings] = useState([])
    const [searchValue, setSearchValue] = useState("")
    const [records, setRecords] = useState([])
    const [filteredRecords, setFilteredRecords] = useState([])
    const [filter, setFilter] = useState({
        buildingId: 'all',
        status: 'all'
    })
    const [loadError, setLoadError] = useState('')
    const navigate = useNavigate()
    const { entityId } = useParams()
    
    useEffect(() => {
        const start = async () => {
            setCurrentUser(await checkAuth(navigate))
            await getMuseum(entityId)
            await getBuildings()
            await getRecords()
        }
        start()
    }, [])

    useEffect(() => {
        search(searchValue)
    }, [searchValue])

    useEffect(() => {
        if (!records.length) {
            return
        }
        
        let filteredRows = records
        
        if (filter.buildingId != 'all') {
            filteredRows = filteredRows.filter(x => x.buildingId === filter.buildingId)
        }
        
        if (filter.status != 'all') {
            filteredRows = filteredRows.filter(x => x.status === filter.status)
        }
        
        setFilteredRecords(filteredRows)
    }, [filter])

    const getMuseum = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-museum/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load museum data: " + (response.data.error || response.data))
                return
            }

            setMuseum(response.data.item)
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const getBuildings = async () => {
        const response = await axios.get(`${apiURL}/get-buildings/${entityId}`)
        setBuildings(response.data.rows)
        setFilteredBuildings(response.data.rows)
    }

    const search = (str) => {
        if (!buildings || !buildings.length) {
            return
        }

        if (!str) {
            setFilteredBuildings(buildings)
            return
        }

        const lowerValue = removeAccents(str.toLowerCase())
        const filteredRows = buildings.filter(row => removeAccents(Object.values(row).join('').toLowerCase()).includes(lowerValue))
        setFilteredBuildings(filteredRows)
    }

    const removeAccents = (str) => {
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    }

    const getRecords = async () => {
        const response = await axios.get(`${apiURL}/get-records/${entityId}`)
        setRecords(response.data.rows || [])
        setFilteredRecords(response.data.rows || [])
    }

    const filterChange = (field, value) => {
        setFilter({
            ...filter,
            [field]: value
        })
    }

    const navigateToNewBuilding = () => {
        navigate(`/museum/${entityId}/documentation/buildings/new`)
    }

    const navigateToViewBuilding = (id) => {
        navigate(`/museum/${entityId}/documentation/buildings/${id}/view`)
    }

    const navigateToViewRecord = (id) => {
        const record = records.find(x => x.id === id)
        navigate(`/museum/${entityId}/documentation/buildings/${record.buildingId}/edit-record/${record.id}`)
    }

    return (
        <>
            <Topbar currentUser={currentUser} />
            <main className="full-width">
                {museum.id &&
                    <>
                        <div>
                            <Link to={`/museum/${museum.id}/documentation`} className="no-decoration">
                                <FaAngleLeft /> Back to Documentation
                            </Link>
                        </div>
                        <header className="mt-2">
                            <div className="float-start">
                                <h1>Building Documentation</h1>
                            </div>
                            <Button variant="primary" size="lg" className="float-end" onClick={navigateToNewBuilding}>
                                <FaPlus /><span className="d-none d-md-inline"> New Building</span>
                            </Button>
                        </header>
                        {filteredBuildings
                            ? <>
                                <Form.Control
                                    placeholder="Building quick search"
                                    onChange={e => setSearchValue(e.target.value)}
                                />
                                <div className="py-2 pl-4">
                                    <small className="float-end text-secondary">
                                        {filteredBuildings.length === buildings.length
                                            ? <>{filteredBuildings.length} buildings</>
                                            : <>{filteredBuildings.length}/{buildings.length} buildings</>
                                        }
                                    </small>
                                </div>
                                <Row>
                                    {filteredBuildings.map(building =>
                                        <Col md="6" lg="3" key={`col-${building.id}`}>
                                            <Card className="listItem clickable mb-4" onClick={() => navigateToViewBuilding(building.id)}>
                                                <div className="headerImage" style={{backgroundImage: `url(${filesURL}/${building.mainImage})`}}></div>
                                                <Card.Body>
                                                    <Card.Title>{building.name}</Card.Title>
                                                    <Card.Text>{building.description}</Card.Text>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    )}
                                </Row>
                            </>
                            : <>No buildings found.</>
                        }
                        <h3 className="mt-4 mb-3">Previous documentation records</h3>
                        <Row>
                            <Col sm="6" md="3">
                                <FloatingLabel controlId="buildingFilter" label="Building" className="mb-3">
                                    <Form.Select
                                        name="building"
                                        value={filter.buildingId}
                                        onChange={e => filterChange('buildingId', e.target.value)}
                                    >
                                        <option key="all" value="all">
                                            All
                                        </option>
                                        {filteredBuildings.map(building => (
                                            <option key={building.id} value={building.id}>
                                                {building.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </FloatingLabel>
                            </Col>
                            <Col sm="6" md="3">
                                <FloatingLabel controlId="statusFilter" label="Status" className="mb-3">
                                    <Form.Select
                                        name="status"
                                        value={filter.status}
                                        onChange={e => filterChange('status', e.target.value)}
                                    >
                                        <option key="all" value="all">
                                            All
                                        </option>
                                        {statusOptions.map(option => (
                                            <option key={option.id} value={option.id}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </FloatingLabel>
                            </Col>
                        </Row>
                        {filteredRecords.length
                            ? <Table striped bordered hover cellPadding="15">
                                <thead>
                                    <tr>
                                        <th>Building</th>
                                        <th>Created at</th>
                                        <th>Created by</th>
                                        <th>Document status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRecords
                                        .map(record => (
                                            <tr key={record.id} className="recordRow">
                                                <td>
                                                    {record.buildingName}
                                                </td>
                                                <td>
                                                    {record.createdAt}
                                                </td>
                                                <td>
                                                    {record.createdBy}
                                                </td>
                                                <td>
                                                    {record.status === "draft" &&
                                                        <Badge bg="warning" text="dark">
                                                            Draft
                                                        </Badge>
                                                    }
                                                    {record.status === "review" &&
                                                        <Badge bg="info" text="dark">
                                                            Submitted for review
                                                        </Badge>
                                                    }
                                                </td>
                                                <td>
                                                    <OverlayTrigger overlay={<Tooltip>Edit record</Tooltip>}>
                                                        <Button variant="outline-primary" className="me-3" onClick={() => navigateToViewRecord(record.id)}>
                                                            <FaEdit />
                                                        </Button>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger overlay={<Tooltip>Print as PDF</Tooltip>}>
                                                        <Button variant="outline-primary" disabled>
                                                            <FaDownload />
                                                        </Button>
                                                    </OverlayTrigger>
                                                </td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </Table>
                            : <>
                                No records found.
                            </>
                        }
                    </>
                }
                {loadError &&
                    <>
                        <Alert key='loadError' variant='danger'>
                            {loadError}
                        </Alert>
                    </>
                }
                <Footer user={currentUser} />
            </main>
        </>
    )
}

export default BuildingList